import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from './LanguageSelector';
import { billingAPI, SubscriptionPlan } from '../services/api';

type RegistrationStep = 'info' | 'plan';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [registrationStep, setRegistrationStep] =
    useState<RegistrationStep>('info');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_name: '',
  });
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null,
  );
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, register } = useAuth();

  useEffect(() => {
    if (!isLogin && registrationStep === 'plan') {
      loadPlans();
    }
  }, [isLogin, registrationStep]);

  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await billingAPI.getPlans();
      setPlans(response.data.plans);
      if (response.data.plans.length > 1) {
        setSelectedPlan(response.data.plans[1]);
      } else if (response.data.plans.length > 0) {
        setSelectedPlan(response.data.plans[0]);
      }
    } catch (err) {
      setError(t('billing.errors.loadPlans'));
    } finally {
      setPlansLoading(false);
    }
  };

  const handleInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.organization_name.trim()) {
      setError(t('auth.organizationRequired'));
      return;
    }
    setRegistrationStep('plan');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        const userData = {
          user: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            password: formData.password,
            role: 'admin',
          },
          organization: formData.organization_name
            ? {
                name: formData.organization_name,
              }
            : undefined,
          plan_id: selectedPlan?.id,
        };
        await register(userData);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) return t('billing.unlimited');
    return limit.toString();
  };

  // Plan selection step
  const renderPlanSelection = () => (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Landing page gradient effect */}
      <div className="absolute inset-0 bg-landing-gradient" />
      <div className="absolute inset-0 bg-hero-glow-intense" />
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Large watermark */}
      <div
        className="absolute bottom-0 left-0 right-0 text-[15rem] font-bold text-slate-800/20 leading-none select-none pointer-events-none text-center"
        style={{ transform: 'translateY(30%)' }}
      >
        GrantEx
      </div>

      <div className="relative z-10 min-h-screen py-16 px-4">
        {/* Header */}
        <div className="absolute top-6 right-6">
          <LanguageSelector />
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Back & Title */}
          <div className="text-center mb-16">
            <button
              onClick={() => setRegistrationStep('info')}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-10 text-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('common.back')}
            </button>

            <h1 className="heading-2 text-white mb-4">
              {t('auth.selectPlanTitle')}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {t('auth.selectPlanSubtitle')}
            </p>

            <div className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
              <svg
                className="w-6 h-6 text-amber-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2L9.5 7H6l1.5 3.5L4 15h4l1.5 3.5L12 22l2.5-3.5L16 15h4l-3.5-4.5L18 7h-3.5L12 2z" />
              </svg>
              <span className="text-amber-300 text-lg font-medium">
                {t('auth.trialIncluded')}
              </span>
            </div>
          </div>

          {/* Plans */}
          {plansLoading ? (
            <div className="flex justify-center py-20">
              <div className="spinner-white w-10 h-10" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {plans.map((plan, index) => (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className={`
                    relative cursor-pointer rounded-3xl p-8 transition-all duration-300
                    animate-fade-up opacity-0
                    ${index === 0 ? 'stagger-1' : index === 1 ? 'stagger-2' : 'stagger-3'}
                    ${
                      selectedPlan?.id === plan.id
                        ? 'bg-white shadow-2xl shadow-indigo-500/10 scale-[1.02] ring-2 ring-indigo-500/50'
                        : 'bg-slate-900/50 border border-slate-700 hover:bg-slate-900 hover:border-indigo-500/30'
                    }
                  `}
                  style={{ animationFillMode: 'forwards' }}
                >
                  {/* Popular badge */}
                  {index === 1 && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="px-5 py-2 bg-indigo-500 text-white text-sm font-bold rounded-full shadow-lg shadow-indigo-500/30">
                        POPULAIRE
                      </span>
                    </div>
                  )}

                  {/* Plan name */}
                  <div className="text-center mb-8">
                    <h3
                      className={`text-2xl font-bold mb-2 ${selectedPlan?.id === plan.id ? 'text-slate-900' : 'text-white'}`}
                    >
                      {plan.name}
                    </h3>
                    {plan.description && (
                      <p
                        className={`text-base ${selectedPlan?.id === plan.id ? 'text-slate-600' : 'text-slate-400'}`}
                      >
                        {plan.description}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-center mb-8">
                    <div className="flex items-baseline justify-center gap-1">
                      <span
                        className={`text-6xl font-bold ${selectedPlan?.id === plan.id ? 'text-indigo-600' : 'text-white'}`}
                      >
                        ${plan.price.toFixed(0)}
                      </span>
                      <span
                        className={`text-xl ${selectedPlan?.id === plan.id ? 'text-slate-500' : 'text-slate-400'}`}
                      >
                        /{t(`billing.interval.${plan.billing_interval}`)}
                      </span>
                    </div>
                    <span
                      className={`text-base ${selectedPlan?.id === plan.id ? 'text-slate-500' : 'text-slate-400'}`}
                    >
                      {t('billing.perUser')}
                    </span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-4">
                    {[
                      {
                        label: `${formatLimit(plan.user_limit)} ${t('billing.limits.users')}`,
                      },
                      {
                        label: `${formatLimit(plan.time_entry_limit)} ${t('billing.limits.timeEntries')}`,
                      },
                      {
                        label: `${formatLimit(plan.ai_analysis_limit)} ${t('billing.limits.aiAnalysis')}`,
                      },
                      ...(plan.features?.map((f) => ({ label: f })) || []),
                    ].map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-center gap-3 text-base ${selectedPlan?.id === plan.id ? 'text-slate-600' : 'text-slate-300'}`}
                      >
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${selectedPlan?.id === plan.id ? 'bg-indigo-100' : 'bg-indigo-500/20'}`}
                        >
                          <svg
                            className={`w-4 h-4 ${selectedPlan?.id === plan.id ? 'text-indigo-600' : 'text-indigo-400'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        {feature.label}
                      </li>
                    ))}
                  </ul>

                  {/* Selection indicator */}
                  {selectedPlan?.id === plan.id && (
                    <div className="absolute top-6 right-6 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/30">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center mb-8">
              <p className="inline-flex items-center gap-3 px-6 py-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-lg">
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !selectedPlan}
              className="btn btn-lg text-xl px-16 bg-indigo-500 text-white hover:bg-indigo-400 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-3">
                  <div className="spinner-white w-6 h-6" />
                  {t('auth.processing')}
                </span>
              ) : (
                <span className="flex items-center gap-3">
                  {t('auth.startTrial')}
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Login/Registration form
  if (!isLogin && registrationStep === 'plan') {
    return renderPlanSelection();
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        {/* Landing page gradient effect - indigo glow at top */}
        <div className="absolute inset-0 bg-landing-gradient" />
        <div className="absolute inset-0 bg-hero-glow-intense" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Large watermark text like landing page */}
        <div
          className="absolute bottom-0 left-0 right-0 text-[20rem] font-bold text-slate-800/30 leading-none select-none pointer-events-none"
          style={{ transform: 'translateY(30%)' }}
        >
          GrantEx
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          {/* Logo - matches landing page style */}
          <div
            className="mb-16 animate-fade-up"
            style={{ animationFillMode: 'forwards' }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#4F7DF3] to-[#6366F1] flex items-center justify-center shadow-lg shadow-[#4F7DF3]/30 border border-[#6366F1]/30">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {t('app.title')}
                </h1>
                <p className="text-sm text-slate-400">{t('app.tagline')}</p>
              </div>
            </div>
          </div>

          {/* Hero text */}
          <div
            className="space-y-8 animate-fade-up stagger-1"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white leading-[1.1] tracking-tight">
              {isLogin ? 'Bienvenue' : 'Commencez'}
              <br />
              <span className="text-slate-400">votre parcours</span>
            </h2>

            <p className="text-xl text-slate-400 max-w-lg leading-relaxed">
              {isLogin
                ? 'Connectez-vous pour accéder à vos feuilles de temps et analyses de conformité SR&ED/CDAE.'
                : "Créez votre compte et optimisez vos crédits d'impôt R&D avec notre plateforme intelligente."}
            </p>

            {/* Feature list */}
            <div className="space-y-5 pt-6">
              {[
                {
                  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                  text: 'Analyse de conformité SR&ED',
                },
                {
                  icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                  text: 'IA pour optimisation CDAE',
                },
                {
                  icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                  text: 'Rapports exportables',
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 text-slate-300 animate-fade-up opacity-0"
                  style={{
                    animationDelay: `${0.3 + idx * 0.1}s`,
                    animationFillMode: 'forwards',
                  }}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#4F7DF3]/20 flex items-center justify-center border border-[#4F7DF3]/20">
                    <svg
                      className="w-5 h-5 text-[#6366F1]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={feature.icon}
                      />
                    </svg>
                  </div>
                  <span className="text-base text-slate-300">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Quebec badge */}
          <div
            className="mt-20 animate-fade-up stagger-5"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            <div className="maple-badge">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2L9.5 7H6l1.5 3.5L4 15h4l1.5 3.5L12 22l2.5-3.5L16 15h4l-3.5-4.5L18 7h-3.5L12 2z" />
              </svg>
              <span>Conçu au Québec</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white px-6 py-16 relative">
        {/* Language selector */}
        <div className="absolute top-6 right-6">
          <LanguageSelector />
        </div>

        {/* Mobile logo */}
        <div className="lg:hidden absolute top-6 left-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#4F7DF3] to-[#6366F1] flex items-center justify-center shadow-lg shadow-[#4F7DF3]/30 border border-[#6366F1]/30">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <span className="text-xl font-bold text-slate-900">
              {t('app.title')}
            </span>
          </div>
        </div>

        <div className="w-full max-w-md">
          {/* Header */}
          <div
            className="text-center mb-12 animate-fade-up"
            style={{ animationFillMode: 'forwards' }}
          >
            <h2 className="heading-3 mb-3 text-slate-600">
              {isLogin ? t('auth.signInTitle') : t('auth.signUpTitle')}
            </h2>
            <p className="text-lg text-slate-600">
              {isLogin
                ? 'Entrez vos identifiants pour continuer'
                : 'Créez votre compte pour commencer'}
            </p>
          </div>

          {/* Form */}
          <form
            className="space-y-6 animate-fade-up stagger-1"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
            onSubmit={isLogin ? handleSubmit : handleInfoSubmit}
          >
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="first_name"
                      className="input-label text-slate-600"
                    >
                      {t('auth.firstName')}
                    </label>
                    <input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required={!isLogin}
                      value={formData.first_name}
                      onChange={handleChange}
                      className="input bg-white text-slate-900"
                      placeholder={t('auth.firstNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="last_name"
                      className="input-label text-slate-600"
                    >
                      {t('auth.lastName')}
                    </label>
                    <input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required={!isLogin}
                      value={formData.last_name}
                      onChange={handleChange}
                      className="input bg-white text-slate-900"
                      placeholder={t('auth.lastNamePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="organization_name"
                    className="input-label text-slate-600"
                  >
                    {t('auth.organizationName')}{' '}
                    <span className="text-indigo-500">*</span>
                  </label>
                  <input
                    id="organization_name"
                    name="organization_name"
                    type="text"
                    required={!isLogin}
                    value={formData.organization_name}
                    onChange={handleChange}
                    className="input bg-white text-slate-900"
                    placeholder={t('auth.organizationPlaceholder')}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="input-label text-slate-600">
                {t('auth.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input bg-white text-slate-900"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="password" className="input-label text-slate-600">
                {t('auth.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                required
                value={formData.password}
                onChange={handleChange}
                className="input bg-white text-slate-900"
                placeholder={t('auth.passwordPlaceholder')}
              />
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 animate-fade-in">
                <svg
                  className="w-6 h-6 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn w-full text-base bg-gradient-to-r from-[#4F7DF3] to-[#6366F1] text-white hover:from-[#5d88f5] hover:to-[#7577f3] shadow-lg shadow-[#4F7DF3]/25 hover:shadow-xl hover:shadow-[#4F7DF3]/30 rounded-full"
            >
              {loading ? (
                <span className="flex items-center gap-3">
                  <div className="spinner w-5 h-5" />
                  {t('auth.processing')}
                </span>
              ) : isLogin ? (
                t('auth.signIn')
              ) : (
                t('auth.continue')
              )}
            </button>
          </form>

          {/* Toggle */}
          <div
            className="mt-10 text-center animate-fade-up stagger-2"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setRegistrationStep('info');
                setSelectedPlan(null);
                setFormData({
                  email: '',
                  password: '',
                  first_name: '',
                  last_name: '',
                  organization_name: '',
                });
              }}
              className="text-[#4F7DF3] hover:text-[#6366F1] text-lg font-semibold transition-colors"
            >
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
            </button>
          </div>

          {/* Footer */}
          <div
            className="mt-12 pt-8 border-t border-slate-200 text-center animate-fade-up stagger-3"
            style={{ animationFillMode: 'forwards', opacity: 0 }}
          >
            <p className="text-sm text-slate-500">
              En continuant, vous acceptez nos{' '}
              <a
                href="#"
                className="text-[#4F7DF3] hover:underline font-medium"
              >
                conditions d'utilisation
              </a>{' '}
              et notre{' '}
              <a
                href="#"
                className="text-[#4F7DF3] hover:underline font-medium"
              >
                politique de confidentialité
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
